/**
 * API Client for Sphero BPS Projects
 * Communicates with Rails backend at /api/bps/v1/projects
 */

import { httpFetch, type HttpResponseLike } from '../http';
import {
  type CreateProjectRequest,
  type CreateProjectResponse,
  type GetProjectResponse,
  type UpdateProjectResponse,
  type UpdateProjectRequest,
  type DeleteProjectRequest,
  type ApiError,
  type BpsProjectVersionFields,
  isBpsTemplateProject,
  parseBpsProjectVersionFields,
  serializeBpsProjectVersionWireFields,
} from './types';

// API base URL from environment variable, with staging as fallback for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://staging-edu.sphero.com';

/** Share code prefix used when the API omits `project_type` (legacy / transitional). */
const TEMPLATE_CODE_PREFIX = 'TMP';

/** JSON body from `/api/bps/v1/projects` before client normalization. */
type WireBpsProjectPayload = BpsProjectVersionFields & {
  id: number;
  title?: string;
  share_code: string;
  project_string: string;
  updated_at: string;
  project_type?: string;
};

function parseProjectStringVersionFields(projectString: string | undefined) {
  if (!projectString) return serializeBpsProjectVersionWireFields({});

  try {
    return serializeBpsProjectVersionWireFields(JSON.parse(projectString));
  } catch (error) {
    console.warn('Unable to parse project version metadata from project_string:', error);
    return serializeBpsProjectVersionWireFields({});
  }
}

function withDerivedTemplateFields<T extends WireBpsProjectPayload>(data: T): T & { project_type: string; is_template: boolean } {
  const projectType = data.project_type ?? (data.share_code.startsWith(TEMPLATE_CODE_PREFIX) ? 'template' : 'user');
  return {
    ...data,
    ...parseBpsProjectVersionFields(data),
    project_type: projectType,
    is_template: isBpsTemplateProject({ project_type: projectType }),
  };
}

class ProjectsApiError extends Error {
  constructor(message: string, public status: number, public response?: ApiError) {
    super(message);
    this.name = 'ProjectsApiError';
  }
}

async function handleResponse<T>(response: HttpResponseLike): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError | undefined;
    try {
      errorData = (await response.json()) as ApiError | undefined;
    } catch {
      // Response body might not be JSON
    }

    throw new ProjectsApiError(errorData?.error || `HTTP ${response.status}: ${response.statusText}`, response.status, errorData);
  }

  // 204 No Content responses don't have a body
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const projectsApi = {
  /**
   * Create a new project on the server
   * @param projectString - The entire project serialized as JSON string
   * @param pin - 4-character collaborator PIN
   * @returns Object containing the generated share_code
   */
  async create(projectString: string, pin: string, title?: string): Promise<CreateProjectResponse> {
    const versionFields = parseProjectStringVersionFields(projectString);
    const response = await httpFetch(`${API_BASE_URL}/api/bps/v1/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pin,
        project_string: projectString,
        ...(title && { title }),
        ...versionFields,
      } as CreateProjectRequest),
      responseType: 'json',
    });

    const data = await handleResponse<WireBpsProjectPayload>(response);
    return withDerivedTemplateFields(data);
  },

  /**
   * Get a project by its share code
   * @param shareCode - 6-character share code
   * @returns Project data with `project_type` from the API and `is_template` derived
   *   (`project_type === "template"`). If `project_type` is missing, inferred as
   *   `template` when `share_code` starts with `TMP`, otherwise `user`.
   */
  async get(shareCode: string): Promise<GetProjectResponse> {
    const response = await httpFetch(`${API_BASE_URL}/api/bps/v1/projects/${shareCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'json',
    });

    const data = await handleResponse<WireBpsProjectPayload>(response);
    return withDerivedTemplateFields(data);
  },

  /**
   * Validate a collaborator PIN for a project
   * @param shareCode - 6-character share code
   * @param pin - 4-character collaborator PIN
   * @returns void - throws error if PIN is invalid
   */
  async validatePin(shareCode: string, pin: string): Promise<void> {
    const response = await httpFetch(`${API_BASE_URL}/api/bps/v1/projects/${shareCode}/validate/${pin}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'json',
    });

    return handleResponse<void>(response);
  },

  /**
   * Update an existing project
   * @param shareCode - 6-character share code
   * @param pin - 4-character collaborator PIN
   * @param projectString - Updated project data as JSON string
   */
  async update(shareCode: string, pin: string, projectString?: string, title?: string): Promise<UpdateProjectResponse | undefined> {
    const requestVersionFields = parseProjectStringVersionFields(projectString);
    const response = await httpFetch(`${API_BASE_URL}/api/bps/v1/projects/${shareCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pin,
        ...(projectString && { project_string: projectString }),
        ...(title && { title }),
        ...requestVersionFields,
      } as UpdateProjectRequest),
      responseType: 'json',
    });

    const data = await handleResponse<unknown>(response);
    const responseVersionFields = parseBpsProjectVersionFields(data);
    if (Object.keys(responseVersionFields).length > 0) {
      return responseVersionFields;
    }

    // 204 / empty bodies omit version metadata; fall back to what we sent.
    const requestMetadata = parseBpsProjectVersionFields(requestVersionFields);
    return Object.keys(requestMetadata).length > 0 ? requestMetadata : undefined;
  },

  /**
   * Delete a project from the server
   * @param shareCode - 6-character share code
   * @param pin - 4-character collaborator PIN
   */
  async delete(shareCode: string, pin: string): Promise<void> {
    const response = await httpFetch(`${API_BASE_URL}/api/bps/v1/projects/${shareCode}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pin,
      } as DeleteProjectRequest),
      responseType: 'json',
    });

    return handleResponse<void>(response);
  },
};

export { ProjectsApiError };
